/**
 * Silent background component — monitors prices + user stream and fires
 * browser notifications. Mounted once in AppRoutes outside any page.
 *
 * Price alerts: driven by live WS price ticks (sub-second latency).
 * Fill / liquidation alerts: driven by /ws/user stream from the indexer.
 */
import { useEffect, useRef } from "react";
import useWallet from "lib/wallets/useWallet";
import { INDEXER_BASE } from "lib/indexer/client";
import {
  loadPrefs,
  loadTriggeredAlerts,
  saveTriggeredAlerts,
  fireNotification,
  type PriceAlert,
} from "lib/notifications/notificationPrefs";
import { subscribe as subscribePrices } from "lib/indexer/useLivePrices";

const WS_BASE = INDEXER_BASE.startsWith("http")
  ? INDEXER_BASE.replace(/^http/, "ws")
  : typeof window !== "undefined"
  ? window.location.origin.replace(/^http/, "ws")
  : "ws://localhost:8011";

export function NotificationsMonitor() {
  const { active, account } = useWallet();
  const triggeredRef = useRef<Record<string, number>>(loadTriggeredAlerts());

  // ── Price alerts (sub-second via WS ticks) ──────────────────────────────
  useEffect(() => {
    const unsubscribe = subscribePrices((prices) => {
      const prefs = loadPrefs();
      if (!prefs.browserEnabled || Notification.permission !== "granted") return;
      const now = Date.now();

      prefs.priceAlerts.forEach((alert: PriceAlert) => {
        if (!alert.enabled) return;
        const current = prices[alert.symbol.toUpperCase()];
        if (current === undefined) return;

        const key = `price_${alert.id}`;
        if ((triggeredRef.current[key] ?? 0) > now - 60_000) return;

        const hit =
          (alert.direction === "above" && current >= alert.targetPrice) ||
          (alert.direction === "below" && current <= alert.targetPrice);

        if (hit) {
          fireNotification(
            `${alert.symbol} Price Alert`,
            `${alert.symbol} is ${alert.direction === "above" ? "above" : "below"} $${alert.targetPrice.toLocaleString()} — now $${current.toLocaleString(undefined, { maximumFractionDigits: 2 })}`,
            key
          );
          triggeredRef.current = { ...triggeredRef.current, [key]: now };
          saveTriggeredAlerts(triggeredRef.current);
        }
      });
    });

    return unsubscribe;
  }, []);

  // ── User stream (fills + liquidations) ─────────────────────────────────
  useEffect(() => {
    if (!active || !account) return;

    let ws: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let closed = false;

    function connect() {
      if (closed) return;
      ws = new WebSocket(`${WS_BASE}/ws/user?address=${account!.toLowerCase()}`);

      ws.onmessage = (event) => {
        try {
          const msg: { type: string; data: any } = JSON.parse(event.data);
          const prefs = loadPrefs();
          if (!prefs.browserEnabled || Notification.permission !== "granted") return;

          if (msg.type === "fill" && prefs.tradeConfirmations) {
            const { action, sizeDelta, price } = msg.data;
            const sizeUsd  = (Number(sizeDelta) / 1e30).toLocaleString(undefined, { maximumFractionDigits: 2 });
            const priceUsd = (Number(price) / 1e30).toLocaleString(undefined, { maximumFractionDigits: 2 });
            fireNotification(
              `Order Filled — ${action === "increase" ? "Position Opened" : "Position Closed"}`,
              `Size $${sizeUsd} at $${priceUsd}`,
              `fill_${msg.data.txHash}`
            );
          }

          if (msg.type === "liquidation") {
            const { size, markPrice } = msg.data;
            const sizeUsd  = (Number(size) / 1e30).toLocaleString(undefined, { maximumFractionDigits: 2 });
            const priceUsd = (Number(markPrice) / 1e30).toLocaleString(undefined, { maximumFractionDigits: 2 });
            fireNotification(
              "⚠️ Position Liquidated",
              `Your position (size $${sizeUsd}) was liquidated at $${priceUsd}`,
              `liq_${msg.data.txHash}`
            );
          }

          if (msg.type === "order_exec" && prefs.tradeConfirmations) {
            const { orderType, executionPrice } = msg.data;
            const priceUsd = executionPrice
              ? (Number(executionPrice) / 1e30).toLocaleString(undefined, { maximumFractionDigits: 2 })
              : "market";
            fireNotification(
              "Limit Order Executed",
              `${orderType === "increase" ? "Increase" : "Decrease"} order filled at $${priceUsd}`,
              `oexec_${msg.data.orderType}_${msg.data.orderIndex}`
            );
          }
        } catch (_) { /* ignore malformed */ }
      };

      ws.onclose = () => {
        ws = null;
        if (!closed) reconnectTimer = setTimeout(connect, 5000);
      };
      ws.onerror = () => { /* close fires next */ };
    }

    connect();

    return () => {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws && ws.readyState !== WebSocket.CONNECTING) ws.close();
    };
  }, [active, account]);

  return null;
}
